// Student Editor - PartCPage1 (Pixel-perfect redesign with exact visual match)
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useActivityContext } from '../index';
import { API_CONFIG } from '../../../../../config/api';

const PartCPage1 = () => {
    const { activity } = useActivityContext();
    const [guidingQuestions, setGuidingQuestions] = useState(['', '', '']);
    const [saveMessage, setSaveMessage] = useState('');

    const teacherFields = activity?.part_c?.page_1?.teacher_fields || {};

    useEffect(() => {
        if (activity?.my_response_part_c?.page_1) {
            const saved = activity.my_response_part_c.page_1.guiding_questions || ['', '', ''];
            setGuidingQuestions(saved.length >= 3 ? saved : [...saved, ...Array(3 - saved.length).fill('')]);
        }
    }, [activity]);

    const saveChanges = async (questions) => {
        try {
            const token = localStorage.getItem('accessToken');
            const apiUrl = API_CONFIG.BASE_URL;

            await axios.put(
                `${apiUrl}/secondary/user/domain-activities/${activity.activity_id}/part-c/page/1/student-fields`,
                { guiding_questions: questions },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setSaveMessage('Saved ✓');
            setTimeout(() => setSaveMessage(''), 2000);
        } catch (error) {
            console.error('Error:', error);
            setSaveMessage('Error');
        }
    };

    const handleChange = (index, value) => {
        const newQuestions = [...guidingQuestions];
        newQuestions[index] = value;
        setGuidingQuestions(newQuestions);
        saveChanges(newQuestions);
    };

    const addQuestion = () => {
        const newQuestions = [...guidingQuestions, ''];
        setGuidingQuestions(newQuestions);
    };

    const removeQuestion = (index) => {
        if (guidingQuestions.length > 1) {
            const newQuestions = guidingQuestions.filter((_, i) => i !== index);
            setGuidingQuestions(newQuestions);
            saveChanges(newQuestions);
        }
    };

    const RoundedPillBox = ({ labelText, children }) => (
        <div style={{ marginBottom: '8px' }}>
            <div style={{
                backgroundColor: '#F4824C',
                color: 'white',
                padding: '8px 20px',
                borderRadius: '25px',
                fontSize: '14px',      // Increased from 11px
                fontWeight: 'bold',
                marginBottom: '5px',
                display: 'inline-block'
            }}>
                {labelText}
            </div>
            <div style={{
                backgroundColor: '#FFE5D5',
                padding: '10px 15px',
                fontSize: '14px',      // Increased from 10px
                lineHeight: '1.4',
                minHeight: '30px',
                border: '1px solid #F4824C',
                borderRadius: '4px',
                color: '#666'
            }}>
                {children || '-'}
            </div>
        </div>
    );

    const ChevronBox = ({ labelText, children, bgColor = '#F4824C', contentBg = '#FFE5D5' }) => (
        <div style={{ marginBottom: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'stretch' }}>
                <div style={{
                    backgroundColor: bgColor,
                    color: 'white',
                    padding: '15px 20px',
                    fontSize: '14px',      // Increased from 11px
                    fontWeight: 'bold',
                    borderRadius: '8px 0 0 8px',
                    minWidth: '200px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    lineHeight: '1.3'
                }}>
                    {labelText}
                </div>
                <div style={{
                    backgroundColor: contentBg,
                    flex: 1,
                    padding: '12px 15px',
                    minHeight: '80px',
                    borderRadius: '0 8px 8px 0',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    fontSize: '14px',      // Increased from 10px
                    lineHeight: '1.4',
                    color: '#666'
                }}>
                    {children}
                </div>
            </div>
        </div>
    );

    return (
        <div style={{
            width: '100%',             // Fluid width instead of fixed 210mm
            maxWidth: '1400px',        // Increased from 1200px
            minHeight: '80vh',         // Dynamic height instead of fixed 297mm
            margin: '0 auto',          // Center without wrapper
            padding: '40px',           // Increased from 20mm for better spacing
            backgroundColor: 'white',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',  // Professional shadow
            borderRadius: '8px',       // Soft corners
            position: 'relative',
            fontFamily: "'Poppins', Arial, sans-serif",  // Updated to Poppins
            boxSizing: 'border-box'    // CRITICAL: prevents padding issues
        }}>
            {saveMessage && (
                <div style={{
                    position: 'fixed',
                    top: '20px',
                    right: '20px',
                    backgroundColor: saveMessage.includes('Error') ? '#f44336' : '#4CAF50',
                    color: 'white',
                    padding: '10px 20px',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    zIndex: 1000
                }}>
                    {saveMessage}
                </div>
            )}

            <div style={{
                backgroundColor: '#F4824C',
                color: 'white',
                padding: '12px 15px',
                fontSize: '28px',      // Increased to match Part F
                fontWeight: 'bold',
                textAlign: 'center',
                marginBottom: '20px',
                letterSpacing: '2px'
            }}>
                PART C
            </div>

            <div style={{
                backgroundColor: '#F4824C',
                color: 'white',
                padding: '10px 15px',
                fontSize: '18px',      // Increased to match Part F
                fontWeight: 'bold',
                marginBottom: '15px'
            }}>
                Problem-Based Inquiry (Individual Work)
            </div>

            <div style={{
                backgroundColor: '#FFE5D5',
                borderLeft: '4px solid #F4824C',
                padding: '8px 12px',
                marginBottom: '12px',
                fontSize: '13px',      // Increased from 10px
                color: '#E65100'
            }}>
                📚 Teacher-provided fields are view-only for students
            </div>

            <RoundedPillBox labelText="Subject(s)">
                {teacherFields.subjects}
            </RoundedPillBox>

            <RoundedPillBox labelText="Curricular goal(s)">
                {teacherFields.curricular_goals}
            </RoundedPillBox>

            <RoundedPillBox labelText="Competency(-ies)">
                {teacherFields.competencies}
            </RoundedPillBox>

            <div style={{ marginBottom: '10px' }}>
                <div style={{
                    backgroundColor: '#F4824C',
                    color: 'white',
                    padding: '12px 15px',
                    fontSize: '15px',      // Increased from 12px
                    fontWeight: 'bold',
                    marginBottom: '5px'
                }}>
                    Pedagogies
                </div>
                <div style={{
                    backgroundColor: '#FFE5D5',
                    padding: '12px 15px',
                    border: '1px solid #F4824C',
                    borderRadius: '4px',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '8px 20px',
                    fontSize: '14px',      // Increased from 10px
                    color: '#666'
                }}>
                    {[
                        'Arts integrated learning', 'Toy/sport/s-based pedagogy',
                        'Heritage/culture crafts pedagogy', 'Experiential learning',
                        'Story-telling/dramatization', 'Inquiry-driven, discovery/ exploratory learning',
                        'Gamification, puzzles (logic, math)', 'Communication-based activities',
                        'Other (please specify)', ''
                    ].map((item, i) => (
                        <span key={i}>{item || '\u00A0'}</span>
                    ))}
                </div>
            </div>

            <ChevronBox labelText="Research prompt/ question/ problem/ challenge/ planned final output">
                {teacherFields.research_prompt}
            </ChevronBox>

            <ChevronBox labelText="Hypothesis">
                {teacherFields.hypothesis}
            </ChevronBox>

            <div style={{ marginBottom: '15px' }}>
                <div style={{ display: 'flex', alignItems: 'stretch', marginBottom: '10px' }}>
                    <div style={{
                        backgroundColor: '#4A90A4',
                        color: 'white',
                        padding: '15px 20px',
                        fontSize: '14px',      // Increased from 11px
                        fontWeight: 'bold',
                        borderRadius: '8px 0 0 8px',
                        minWidth: '200px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                    }}>
                        Guiding questions
                        <br />
                        <span style={{ fontSize: '18px', marginTop: '3px' }}>↓</span>
                    </div>
                    <div style={{
                        backgroundColor: '#B3D9E8',
                        flex: 1,
                        padding: '15px',
                        borderRadius: '0 8px 8px 0'
                    }}>
                        {guidingQuestions.map((q, i) => (
                            <div key={i} style={{ display: 'flex', gap: '10px', marginBottom: '8px', alignItems: 'center' }}>
                                <span style={{ fontWeight: 'bold', fontSize: '14px', minWidth: '15px' }}>•</span>
                                <input
                                    type="text"
                                    value={q}
                                    onChange={(e) => handleChange(i, e.target.value)}
                                    placeholder={`Question ${i + 1}`}
                                    style={{
                                        flex: 1,
                                        padding: '8px',
                                        border: '1px solid #4A90A4',
                                        borderRadius: '3px',
                                        fontSize: '14px',      // Increased from 10px
                                        backgroundColor: 'white'
                                    }}
                                />
                                {guidingQuestions.length > 1 && (
                                    <button
                                        onClick={() => removeQuestion(i)}
                                        style={{
                                            padding: '4px 8px',
                                            backgroundColor: '#f44336',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '3px',
                                            cursor: 'pointer',
                                            fontSize: '10px'
                                        }}
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        ))}
                        <button
                            onClick={addQuestion}
                            style={{
                                marginTop: '8px',
                                padding: '6px 12px',
                                backgroundColor: '#4A90A4',
                                color: 'white',
                                border: 'none',
                                borderRadius: '3px',
                                cursor: 'pointer',
                                fontSize: '10px',
                                fontWeight: 'bold'
                            }}
                        >
                            + Add Question
                        </button>
                    </div>
                </div>
            </div>

            <div style={{
                textAlign: 'center',
                fontSize: '11px',
                color: '#666',
                fontWeight: 'bold',
                marginTop: '30px'
            }}>
                19
            </div>
        </div>
    );
};

export default PartCPage1;
